import { useState, useEffect, useCallback } from "react";
import { DistinctValue } from "../../../../types";
import {
  splitValue as apiSplitValue,
  SplitValueItem,
  EntityFieldItem,
} from "../../../../services/cleaning-data-service";
import { useDialogs } from "../../../../../dialog/use-dialog";
import { useNotification } from "../../../../../../context/notification-provider";

export interface SplitEntry {
  id: number;
  newValue: string;
  targetField: EntityFieldItem | null;
}

interface UseSplitValuesParams {
  fieldId: number | undefined;
  selectedItem: DistinctValue | null;
  availableFields: EntityFieldItem[] | undefined;
  refreshDistinctValues: () => void;
}

interface UseSplitValuesReturn {
  splitEntries: SplitEntry[];
  handleAddSplit: () => void;
  handleRemoveSplit: (id: number) => void;
  handleValueChange: (id: number, value: string) => void;
  handleFieldChange: (id: number, targetField: EntityFieldItem | null) => void;
  handleSplit: () => Promise<void>;
  handleCancel: () => void;
  handleCopyValue: () => Promise<void>;
}

export const useSplitValues = ({
  fieldId,
  selectedItem,
  availableFields,
  refreshDistinctValues,
}: UseSplitValuesParams): UseSplitValuesReturn => {
  const { confirm } = useDialogs();
  const { showNotification } = useNotification();
  const [splitEntries, setSplitEntries] = useState<SplitEntry[]>([]);
  const [nextId, setNextId] = useState(1);

  // Initialize with two entries when item is selected. Value 1 starts as the
  // selected value in the current field, so the user only trims it down to the
  // part that stays rather than retyping it.
  useEffect(() => {
    if (selectedItem && fieldId !== undefined) {
      const currentField =
        availableFields?.find((f) => f.id === fieldId) || null;
      setSplitEntries([
        { id: 0, newValue: selectedItem.value, targetField: currentField },
        { id: 1, newValue: "", targetField: null },
      ]);
      setNextId(2);
    } else {
      setSplitEntries([]);
      setNextId(1);
    }
  }, [selectedItem, fieldId, availableFields]);

  const handleAddSplit = useCallback(() => {
    setSplitEntries((prev) => [
      ...prev,
      { id: nextId, newValue: "", targetField: null },
    ]);
    setNextId((prev) => prev + 1);
  }, [nextId]);

  const handleRemoveSplit = useCallback((id: number) => {
    setSplitEntries((prev) => {
      if (prev.length > 2) {
        return prev.filter((entry) => entry.id !== id);
      }
      return prev;
    });
  }, []);

  const handleValueChange = useCallback((id: number, value: string) => {
    setSplitEntries((prev) =>
      prev.map((entry) =>
        entry.id === id ? { ...entry, newValue: value } : entry,
      ),
    );
  }, []);

  const handleFieldChange = useCallback(
    (id: number, targetField: EntityFieldItem | null) => {
      setSplitEntries((prev) =>
        prev.map((entry) =>
          entry.id === id ? { ...entry, targetField } : entry,
        ),
      );
    },
    [],
  );

  const resetToInitialState = useCallback(
    (firstValue: string) => {
      if (fieldId !== undefined) {
        const currentField =
          availableFields?.find((f) => f.id === fieldId) || null;
        setSplitEntries([
          { id: nextId, newValue: firstValue, targetField: currentField },
          { id: nextId + 1, newValue: "", targetField: null },
        ]);
        setNextId((prev) => prev + 2);
      }
    },
    [fieldId, availableFields, nextId],
  );

  const handleSplit = useCallback(async () => {
    if (fieldId === undefined) {
      showNotification({
        message: "No field selected.",
        severity: "error",
        duration: 3000,
      });
      return;
    }

    if (!selectedItem) {
      showNotification({
        message: "Please select a value to split.",
        severity: "warning",
        duration: 3000,
      });
      return;
    }

    // Validate all entries
    const hasEmptyValues = splitEntries.some(
      (entry) => entry.newValue.trim() === "",
    );
    if (hasEmptyValues) {
      showNotification({
        message: "All split values must have a value.",
        severity: "warning",
        duration: 3000,
      });
      return;
    }

    const hasEmptyFields = splitEntries.some(
      (entry) => entry.targetField === null,
    );
    if (hasEmptyFields) {
      showNotification({
        message: "All split values must have a target field selected.",
        severity: "warning",
        duration: 3000,
      });
      return;
    }

    try {
      const result = await confirm(
        `Are you sure you want to split "${selectedItem.value}" into ${splitEntries.length} value${splitEntries.length > 1 ? "s" : ""}?`,
        {
          title: "Confirm Split Value",
          okText: "Split",
          cancelText: "Cancel",
          severity: "info",
        },
      );

      if (result === true) {
        try {
          const firstEntry = splitEntries[0];
          const additionalEntries = splitEntries.slice(1);

          const splitRequest: SplitValueItem = {
            value: selectedItem.value,
            newValue: firstEntry.newValue.trim(),
            columnDetails: additionalEntries.map((entry) => ({
              columnId: entry.targetField?.id ?? 0,
              value: entry.newValue.trim(),
            })),
          };

          await apiSplitValue(fieldId, splitRequest);

          refreshDistinctValues();
          showNotification({
            message: "Value split successfully.",
            severity: "success",
            duration: 3000,
          });
          // The original value has just been split away, so don't offer it again.
          resetToInitialState("");
        } catch (err) {
          showNotification({
            message: `Failed to split value. Reason: ${(err as Error).message}`,
            severity: "error",
            duration: 3000,
          });
        }
      }
    } catch (err) {
      showNotification({
        message: `Failed to show confirmation dialog. ${(err as Error).message}`,
        severity: "error",
        duration: 3000,
      });
    }
  }, [
    fieldId,
    selectedItem,
    splitEntries,
    showNotification,
    confirm,
    refreshDistinctValues,
    resetToInitialState,
  ]);

  const handleCancel = useCallback(() => {
    resetToInitialState(selectedItem?.value ?? "");
  }, [resetToInitialState, selectedItem]);

  const handleCopyValue = useCallback(async () => {
    if (!selectedItem) return;

    try {
      await navigator.clipboard.writeText(selectedItem.value);
    } catch (err) {
      showNotification({
        message: `Failed to copy value. ${(err as Error).message}`,
        severity: "error",
        duration: 2000,
      });
    }
  }, [selectedItem, showNotification]);

  return {
    splitEntries,
    handleAddSplit,
    handleRemoveSplit,
    handleValueChange,
    handleFieldChange,
    handleSplit,
    handleCancel,
    handleCopyValue,
  };
};
